"use strict";

import { getGlobal } from "../compat";
import HttpApi from "i18next-http-backend";
import i18n from "i18next";
import { Lang } from "./langs.js";

/** Targets that get `localize()` after resource reload. Managed via {@link registerLocalizeTarget} / {@link unregisterLocalizeTarget}. */
const localizeTargets = new Set();

/** Resolves when the most recent `languageChanged` reload + localize cycle completes, or `null` if no cycle is in progress. */
let pendingLocalize = null;

// On any i18next language change, reload resources and localize all registered targets. Targets are responsible for
// dispatching their own viewer-level events from within localize() if needed.
i18n.on('languageChanged', () => {
  if (!Autodesk?.Viewing?.i18n?.reloadResources) return;

  const cycle = Autodesk.Viewing.i18n.reloadResources().
  then(() => {
    // Per-target localize so subclasses, e.g., GuiViewer3D, run their extras. Each base localize() also calls
    // i18n.localize(), so N targets repeat the document [data-i18n] pass. No targets yet → one global pass.
    for (const target of localizeTargets) {target.localize();}
    if (!localizeTargets.size) Autodesk.Viewing.i18n.localize();
  }).
  catch((error) => {console.warn('[i18init] language reload/localize failed:', error);}).
  finally(() => {if (pendingLocalize === cycle) pendingLocalize = null;});
  pendingLocalize = cycle;
});

/**
 * Registers a target for post-reload `localize()`. Pair with {@link unregisterLocalizeTarget}.
 * @param {{ localize: function(): void }} target
 * @private
 */
export function registerLocalizeTarget(target) {
  localizeTargets.add(target);
}

/**
 * Unregisters a target from post-reload `localize()`. Pair with {@link registerLocalizeTarget}.
 * @param {{ localize: function(): void }} target - Same reference passed to {@link registerLocalizeTarget}.
 * @private
 */
export function unregisterLocalizeTarget(target) {
  localizeTargets.delete(target);
}

export var extendLocalization = function (locales) {
  if (locales !== null && typeof locales === "object") {
    Object.keys(locales).forEach(function (language) {
      Autodesk.Viewing.i18n.addResourceBundle(
        language,
        "allstrings",
        locales[language],
        true,
        true
      );
    });
    return true;
  }
  return false;
};

/**
 * Changes the active language, reloads localization resources, and re-localizes all registered targets.
 * @param {string} language - BCP 47 language tag (e.g. `'en'`, `'de'`, `'cs'`).
 * @param {function(): void} [callback] - Runs after reload and localize complete.
 * @returns {Promise<void>}
 */
export var setLanguage = function (language, callback) {
  getGlobal().LOCALIZATION_REL_PATH = "res/locales/" + language + "/";
  return Autodesk.Viewing.i18n.changeLanguage(language).
  then(() => pendingLocalize) // Intentional late read: concurrent calls both chain onto the latest reload cycle.
  .then(() => callback?.());
};


/**
 * Initialize language for localization. The corresponding string files will get downloaded.
 */
export var initializeLocalization = function (options) {
  const global = getGlobal();
  const _window = global;
  const _document = _window && _window.document;
  Autodesk.Viewing.i18n.localize = function (domElement) {
    // Parse data
    function parse(data) {
      try {
        const maybeArray = JSON.parse(data);
        if (Array.isArray(maybeArray)) return maybeArray;
      } catch (e) {

        // skip to return below
      }return [null, data];
    }

    var rootNode = domElement || _document;
    Array.prototype.forEach.call(rootNode.querySelectorAll('[data-i18n]'), function (element) {
      var dataToTranslate = element.getAttribute('data-i18n');
      const [attributeName, stringToTrans, params] = parse(dataToTranslate);

      const translatedString = Autodesk.Viewing.i18n.translate(stringToTrans, params);
      if (translatedString)
      {
        if (attributeName) {
          element.setAttribute(attributeName, translatedString);
        } else
        {
          // Attribute Name is always null.
          // If the element has a placeholder, it's an edit box, no need to translate the textContent.
          if (element.placeholder) {
            element.placeholder = translatedString;
          } else {
            element.textContent = translatedString;
          }
        }
      } else

      {
        if (attributeName) {
          element.setAttribute(attributeName, stringToTrans);
        } else
        {
          element.textContent = stringToTrans;
        }
      }
    });
  };
  var language = options && options.language || navigator.language;
  language = Lang.getSupported(language);

  Autodesk.Viewing.i18n.use(HttpApi).init({
    lng: language,
    backend: {
      loadPath: Autodesk.Viewing.Private.getResourceUrl('res/locales/{{lng}}/{{ns}}.json')
    },
    load: 'currentOnly',
    ns: 'allstrings',
    defaultNS: 'allstrings',
    fallbackLng: "en",
    debug: false,
    useCookie: false,
    interpolation: {
      prefix: "%(",
      suffix: ")",
      escapeValue: false
    }
  });
  return setLanguage(language);
};